import { useCallback, useEffect, useMemo, useState } from "react";

import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ChevronDown, Loader2, ScrollText } from "lucide-react";

import { AuditLogDetailSheet } from "@/features/audit-logs/presentation/components/AuditLogDetailSheet";
import { DataTable } from "@/shared/components/data/DataTable";
import { DataToolbar } from "@/shared/components/data/DataToolbar";
import { StatusPill, type StatusTone } from "@/shared/components/data/StatusPill";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useQueryState } from "@/shared/hooks/use-query-state";
import { cn } from "@/shared/lib/utils";
import { useListAuditLogsQuery } from "@/store/apis/adminApi.enhanced";
import type { AuditLog } from "@/store/apis/adminApi.enhanced";

const PAGE_SIZE = 50;

/**
 * Audit logs — tabla acumulativa con cursor pagination.
 *
 * Decisiones:
 *   - Cursor-based: no hay offset/pages. Botón "Cargar más" al pie.
 *   - URL params persisten los filtros (action, resource_type, from, to).
 *     NO persistimos el cursor — recargar la URL siempre empieza desde
 *     el head.
 *   - Al cambiar un filtro se resetea el stack de resultados.
 *   - Click en fila abre el detail sheet con before/after + metadata.
 */
export function AuditLogsPage() {
  const { get, set } = useQueryState();

  const action = get("action", "");
  const resourceType = get("resource_type", "");
  const fromParam = get("from", "");
  const toParam = get("to", "");

  const filtersActive =
    Boolean(action) || Boolean(resourceType) || Boolean(fromParam) || Boolean(toParam);

  // Acumulamos páginas en memoria — cursor pagination.
  const [cursor, setCursor] = useState<number | undefined>(undefined);
  const [accumulated, setAccumulated] = useState<AuditLog[]>([]);

  const { data, isLoading, isFetching, isError, error, refetch } = useListAuditLogsQuery({
    limit: PAGE_SIZE,
    cursor,
    action: action || undefined,
    resourceType: resourceType || undefined,
    from: fromParam ? new Date(fromParam).toISOString() : undefined,
    to: toParam ? new Date(toParam).toISOString() : undefined,
  });

  // Al cambiar los filtros, reseteamos acumulado + cursor.
  useEffect(() => {
    setCursor(undefined);
    setAccumulated([]);
  }, [action, resourceType, fromParam, toParam]);

  // Cada vez que llegan items nuevos, los agregamos al accumulated
  // (deduplicando por id — el back puede retornar el borde del cursor).
  useEffect(() => {
    if (!data?.items) return;
    setAccumulated((prev) => {
      if (cursor === undefined) return data.items;
      const seen = new Set(prev.map((x) => x.id));
      const nuevos = data.items.filter((x) => !seen.has(x.id));
      return [...prev, ...nuevos];
    });
  }, [data, cursor]);

  const loadMore = useCallback(() => {
    if (data?.next_cursor !== null && data?.next_cursor !== undefined) {
      setCursor(data.next_cursor);
    }
  }, [data?.next_cursor]);

  const resetAll = () => {
    set({ action: "", resource_type: "", from: "", to: "" });
  };

  // Detail state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLog, setDetailLog] = useState<AuditLog | null>(null);
  const openDetail = (log: AuditLog) => {
    setDetailLog(log);
    setDetailOpen(true);
  };

  const columns = useMemo<ColumnDef<AuditLog>[]>(
    () => [
      {
        id: "when",
        header: "Momento",
        cell: ({ row }) => {
          const d = new Date(row.original.created_at);
          return (
            <div className="min-w-0">
              <p className="font-mono text-[12px] font-medium tabular-nums text-foreground">
                {format(d, "d MMM yyyy", { locale: es })}
              </p>
              <p className="font-mono text-[11px] text-muted-foreground">
                {format(d, "HH:mm:ss", { locale: es })}
              </p>
            </div>
          );
        },
      },
      {
        id: "actor",
        header: "Actor",
        cell: ({ row }) => {
          const l = row.original;
          const email = l.actor_email ?? l.actor_role ?? "system";
          const tone: StatusTone =
            l.actor_role === "system" || l.actor_role === "service_role" ? "muted" : "neutral";
          return (
            <div className="min-w-0">
              <p className="truncate text-[13px] font-medium text-foreground">{email}</p>
              {l.actor_role ? (
                <StatusPill tone={tone} size="sm" dot={false} className="mt-0.5">
                  {l.actor_role}
                </StatusPill>
              ) : null}
            </div>
          );
        },
      },
      {
        id: "action",
        header: "Acción",
        cell: ({ row }) => (
          <code className="rounded bg-muted/60 px-1.5 py-0.5 font-mono text-[11.5px] text-foreground">
            {row.original.action}
          </code>
        ),
      },
      {
        id: "resource",
        header: "Recurso",
        cell: ({ row }) => {
          const t = row.original.resource_type;
          const id = row.original.resource_id;
          if (!t) return <span className="font-mono text-[12px] text-muted-foreground">—</span>;
          return (
            <div className="min-w-0 font-mono text-[12px]">
              <span className="text-foreground">{t}</span>
              {id ? (
                <span className="ml-1 truncate text-muted-foreground">
                  · {id.length > 10 ? `${id.slice(0, 8)}…` : id}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        id: "ip",
        header: "IP",
        cell: ({ row }) => (
          <span className="font-mono text-[11.5px] text-muted-foreground">
            {row.original.ip_address ?? "—"}
          </span>
        ),
      },
    ],
    [],
  );

  const hasMore = data?.next_cursor !== null && data?.next_cursor !== undefined;

  return (
    <>
      <PageHeader
        kicker="Trazabilidad"
        title="Auditoría"
        description="Registro inmutable de operaciones del backoffice y del backend. Útil para investigar incidentes y cumplir con compliance."
      />

      <DataToolbar
        filters={
          <>
            <Input
              value={action}
              onChange={(e) => set({ action: e.target.value })}
              placeholder="Acción (ej. advice.update)"
              className="h-10 w-[240px] font-mono text-[12.5px]"
            />
            <Input
              value={resourceType}
              onChange={(e) => set({ resource_type: e.target.value })}
              placeholder="Recurso (ej. advice)"
              className="h-10 w-[180px] font-mono text-[12.5px]"
            />
            <DateRangeInput
              from={fromParam}
              to={toParam}
              onChange={(f, t) => set({ from: f, to: t })}
            />
            {filtersActive ? (
              <Button variant="ghost" size="sm" onClick={resetAll}>
                Limpiar
              </Button>
            ) : null}
          </>
        }
        actions={
          isFetching && !isLoading ? (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground",
              )}
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              Cargando
            </span>
          ) : null
        }
      />

      <DataTable
        columns={columns}
        data={accumulated.length > 0 ? accumulated : data?.items}
        loading={isLoading && accumulated.length === 0}
        error={isError ? error : undefined}
        onRetry={() => void refetch()}
        onRowClick={openDetail}
        getRowId={(row) => String(row.id)}
        filtersActive={filtersActive}
        empty={{
          icon: <ScrollText className="h-6 w-6" />,
          title: "Sin eventos todavía",
          description: "Cuando ocurran operaciones auditables, aparecerán aquí.",
        }}
        noResults={{
          actions: (
            <Button variant="outline" size="sm" onClick={resetAll}>
              Limpiar filtros
            </Button>
          ),
        }}
      />

      {/* Load more / summary */}
      {accumulated.length > 0 ? (
        <div className="mt-4 flex items-center justify-between gap-3">
          <p className="font-mono text-[11px] tabular-nums text-muted-foreground">
            Mostrando <span className="font-medium text-foreground">{accumulated.length}</span>{" "}
            evento
            {accumulated.length === 1 ? "" : "s"}
          </p>
          {hasMore ? (
            <Button variant="outline" size="sm" onClick={loadMore} disabled={isFetching}>
              {isFetching ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Cargando…
                </>
              ) : (
                <>
                  Cargar más
                  <ChevronDown className="h-3.5 w-3.5" />
                </>
              )}
            </Button>
          ) : (
            <span className="font-mono text-[11px] text-muted-foreground">Fin de la lista</span>
          )}
        </div>
      ) : null}

      <AuditLogDetailSheet open={detailOpen} onOpenChange={setDetailOpen} log={detailLog} />
    </>
  );
}

/* ────────────────── Date range ────────────────── */

function DateRangeInput({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Select
        value={from || to ? "custom" : "all"}
        onValueChange={(v) => {
          if (v === "all") {
            onChange("", "");
            return;
          }
          const now = new Date();
          const start = new Date(now);
          if (v === "today") start.setHours(0, 0, 0, 0);
          if (v === "7d") start.setDate(now.getDate() - 7);
          if (v === "30d") start.setDate(now.getDate() - 30);
          onChange(start.toISOString().slice(0, 16), "");
        }}
      >
        <SelectTrigger className="h-10 w-[150px] text-[13px]">
          <SelectValue placeholder="Fecha" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todas las fechas</SelectItem>
          <SelectItem value="today">Hoy</SelectItem>
          <SelectItem value="7d">Últimos 7 días</SelectItem>
          <SelectItem value="30d">Últimos 30 días</SelectItem>
          <SelectItem value="custom">Rango custom</SelectItem>
        </SelectContent>
      </Select>
      {from || to ? (
        <>
          <Input
            type="datetime-local"
            value={from}
            onChange={(e) => onChange(e.target.value, to)}
            aria-label="Desde"
            className="h-10 w-[180px] font-mono text-[12px]"
          />
          <span className="text-muted-foreground">→</span>
          <Input
            type="datetime-local"
            value={to}
            onChange={(e) => onChange(from, e.target.value)}
            aria-label="Hasta"
            className="h-10 w-[180px] font-mono text-[12px]"
          />
        </>
      ) : null}
    </div>
  );
}
